/* eslint-disable max-lines-per-function, max-params, max-lines */
import { track } from "jiffy-analytics";
import { EventBus, Events, store } from "jiffy-store";
import { isStyleVisible } from "common/utils/ui/element";
import { on as onEvent } from "common/utils/ui/event";
import { getQueryParams } from "common/utils/browser";
import { trackRudderstackBlanksProductPriceChanged } from "common/utils/rudderstack/tracker";
import MadeStore from "components/jiffy/vue-product-details-print/store/store";
import initFavorite from "components/jiffy/product-details-favorite/product-details-favorite";
import initDeliveryDateWithCurrentCart from "components/jiffy/product-details-delivery-date-with-current-cart/product-details-delivery-date-with-current-cart";
import initNotifyOnRestockModal from "components/jiffy/product-details-notify-on-restock-modal/product-details-notify-on-restock-modal";
import initNotifyOnRestockSignInModal from "components/jiffy/product-details-notify-on-restock-sign-in/product-details-notify-on-restock-sign-in";
import { triggerPdpVisitEvent } from "./analytics";
import showProduct from "./show-product";
import productPage from "./product-page";
import { extractPriority } from "./extract-priority";
import renderOneQtyGrid from "./product-details-load-qty-grid";
import updateSellingPrices from "./update-variant-selling-price";
import updateFlashStock from "./update-flash-stock";
import "components/jiffy/product-details-quantity-form-row/product-details-quantity-form-row";
import "components/jiffy/zipcode-input/zipcode-input";
import "components/jiffy/product-details-dtf-discount-banner/product-details-dtf-discount-banner";

import "./product-details-quantity-form.scss";

const $getQtyInputs = $form => $form.querySelectorAll(".js-grid-input");
const $getItems = () => document.querySelectorAll(".js-product-details-quantity-form-row-item");

const fetchGridCallback = activeColor => {
  store.pdp.change({
    isSettingPDPBadges: true,
    activeColor,
    isSettingGridInventory: true
  });
  productPage.cacheMobileGridDom();
  showProduct.changeEventProductQuantityInputs();
  showProduct.productSelectedColor.setActiveColor(showProduct.getActiveColorName());
  triggerPdpVisitEvent(activeColor);

  EventBus.publish(Events.MOBILE_GRID_LOADED, this, {
    color: activeColor
  });
};

const initWithDynamicData = () => {
  if (document.pdpDynamicData === undefined) {
    document.pdpDynamicData = {};
  }

  initDeliveryDateWithCurrentCart(
    document.pdpDynamicData.zipcode,
    document.pdpDynamicData.isAddressExists
  );

  if (document.pdpDynamicData.tier) {
    EventBus.publish(Events.ORDER_TIER_CHANGED, { tier: document.pdpDynamicData.tier });
  }

  store.pdp.change({
    isSettingPDPBadges: true,
    activeColor: document.pdpDynamicData.active_color,
    isSettingGridInventory: true
  });

  if (document.pdpDynamicData.signed_in) {
    if (document.pdpDynamicData.restock_modal) {
      initNotifyOnRestockModal();
    }
  } else {
    initNotifyOnRestockSignInModal();
  }

  if (document.pdpDynamicData.cart_total) {
    store.pdp.change({ originalCartTotal: parseFloat(document.pdpDynamicData.cart_total) });
  }

  initFavorite();
};

const fetchGrid = async () => {
  if (window.dynamicDataLoaded) {
    initWithDynamicData();
  } else {
    document.addEventListener("dynamicDataLoaded", initWithDynamicData, false);
  }
};

const initMobileGrid = async productId => {
  const $wrapper = document.getElementById("js-product-mobile-grid");
  let isReady = false;

  EventBus.on(Events.PRODUCT_COLOR_SELECTED, () => {
    if (!isReady) {
      $wrapper.classList.add("product-details-variants-form__grid-wrapper--loading");
    }
  });

  const { loaded } = store.get(state => ({
    loaded: state.ui.inventoryLoaded,
    loading: state.ui.inventoryLoading
  }));

  if (loaded) {
    await fetchGrid();
    $wrapper.classList.remove("product-details-variants-form__grid-wrapper--loading");
    isReady = true;
  } else {
    store.pdp.change({ isFetchingInventory: true, fetchingSlug: productId });

    const listener = store.ui.changed((_, { data }) => {
      if (data.inventoryLoaded) {
        fetchGrid().then(() => {
          $wrapper?.classList?.remove("product-details-variants-form__grid-wrapper--loading");
          isReady = true;
        });
        listener && listener();
      }
    });
  }
};

const initSeeSimilars = () => {
  const eventCallback = event => {
    // us
    const $relatedProducts = document.querySelectorAll(".js-related-comparables-products");
    const $visibleRelatedProduct = [...$relatedProducts].find($element => isStyleVisible($element));
    const { mixpanelEvent, mixpanelData } = event.target.dataset;
    const parsedMixpanelData = JSON.parse(mixpanelData);

    track(mixpanelEvent, parsedMixpanelData);

    if ($visibleRelatedProduct) {
      $visibleRelatedProduct.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });

      setTimeout(() => {
        $visibleRelatedProduct.querySelector("a")?.focus();
      }, 750);

      return false;
    }

    // ca
    const $similarTabTrigger = document.querySelectorAll(".js-related-products-similar-handler");
    const $visibleSimilarTabTrigger = [...$similarTabTrigger].find($element =>
      isStyleVisible($element)
    );

    if ($visibleSimilarTabTrigger) {
      // open similar tab
      // @todo: implement with `store.event` mechanism
      $visibleSimilarTabTrigger.click();
      // scroll into view
      $visibleSimilarTabTrigger.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      setTimeout(() => {
        $visibleSimilarTabTrigger.focus();
      }, 750);
    }

    return false;
  };

  onEvent("click", ".js-product-see-similars-link", eventCallback, true);
};

const initFocusHandler = () => {
  onEvent(
    "focus",
    ".js-quantity-form-input",
    () => {
      const popup = document.querySelector(".js-jiffy-first-badge-popup");
      if (popup) {
        popup.classList.remove("open");
      }
    },
    true
  );
};

const initQtyFieldsValidator = () => {
  const inputsCount = {};

  const eventCallback = ({ target }) => {
    // allows only digits without leading 0
    const $target = target;
    $target.value = target.value.replace(/[^0-9]/g, "").replace(/^0/, "");

    // mixpanel tracking
    if ($target.dataset.made === "true") {
      const madeMode = MadeStore.getters["app/madeMode"];
      const textContent =
        madeMode === "print_default" ? "Only Front or Back" : "Both Front and Back";
      const { size, color, amount, variantId } = $target.dataset;

      inputsCount[size] = (inputsCount[size] || 0) + 1;
      track("made_pdp_qty_changed", {
        quantity_value: Number($target.value),
        side_toggle_state: textContent,
        size,
        number_of_user_changes: inputsCount
      });
      trackRudderstackBlanksProductPriceChanged({
        variantId,
        price: amount,
        quantity: Number($target.value),
        variant: color
      });
    }
  };

  onEvent("input", ".js-quantity-form-input", eventCallback, true);
};

const initInputChangeHandler = () => {
  EventBus.on(
    Events.PRODUCT_INPUT_CHANGED,
    ({ target: form }, totalValue, colorName, variantId, variantsQuantity) => {
      productPage.variantPricesDataLoadPromise().then(() => {
        const bulkPricePriority = extractPriority(form, variantId);

        EventBus.publish(Events.ORDER_TIER_CHANGED, { tier: bulkPricePriority });

        updateSellingPrices(bulkPricePriority, form, {
          addedItemsAmount: totalValue,
          inputVariantId: variantId,
          colorName,
          variantId,
          variantsQuantity
        });
      });
    }
  );
};

export const rerenderPrices = variantId => {
  if (!variantId) return;
  const $form = document.querySelector(".js-product-details-quantity-form");
  if (!$form) return;

  const urlParams = new URLSearchParams(window.location.search);
  const colorName = urlParams.get("ac");
  const bulkPricePriority = extractPriority($form, variantId);

  const totalValue = Array.from($getQtyInputs($form)).reduce((acc, elem) => {
    const inputValue = parseInt(elem.value, 10);
    const parsedValue = !Number.isNaN(inputValue) ? inputValue : 0;
    return acc + parsedValue;
  }, 0);

  updateSellingPrices(bulkPricePriority, $form, {
    addedItemsAmount: totalValue,
    colorName,
    variantId
  });
};

const setDefaultPriceHighlight = () => {
  const params = getQueryParams();
  const selectedSize = params.size;
  $getItems().forEach($item => {
    const $price = $item.querySelector(".js-product-details-quantity-form-row-amount");
    if (!$price) return;

    let shouldUpdatePrice = false;
    if (selectedSize) {
      shouldUpdatePrice = $item.dataset.size?.split(",").includes(selectedSize.toUpperCase());
    } else {
      shouldUpdatePrice =
        parseInt($item.dataset.groupIndex, 10) === 0 &&
        parseInt($price.dataset.priceIndex, 10) === 0;
    }

    if (shouldUpdatePrice) {
      $price.dataset.actualPrice = $item.dataset.amount;
      $price.classList.add("product-details-quantity-form-row__actual-price--big");
    }
  });
};

export const productPageLoadPrices = data => {
  productPage.loadPrices(data);
};

export default () => {
  const $wrapper = document.getElementById("product-details-variants-form");
  if (!$wrapper) return;

  showProduct.init();
  const activeColor = showProduct.getActiveColorName();
  const productId = document.querySelector("[name='product_id']").value;

  initMobileGrid(productId);
  renderOneQtyGrid(productId, activeColor, fetchGridCallback);

  if (activeColor) {
    showProduct.setActiveColorName(activeColor);
  }

  if (productId && document.getElementById("product-page-container")) {
    store.pdp.changed((_, { data }) => {
      if (data.loadedPrices) {
        productPageLoadPrices(data.loadedPrices);
        setDefaultPriceHighlight();
      }
    });
  }

  triggerPdpVisitEvent(activeColor);
  initSeeSimilars();
  initFocusHandler();
  initQtyFieldsValidator();
  initInputChangeHandler();

  EventBus.on(Events.SAVED_CART_SELECTED, () => {
    const form = document.querySelector(".js-product-details-quantity-form");
    const currentColorName = document.querySelector("#active-color-name").value;
    const input = document.querySelector(
      `#product-details-variants-form input[data-color="${currentColorName}"]`
    );
    const { variantId } = input.dataset;
    const bulkPricePriority = extractPriority(form, variantId);

    updateSellingPrices(bulkPricePriority, form, {
      colorName: currentColorName,
      variantId
    });
    updateFlashStock();
  });

  store.pdp.change({ isSettingPDPBadges: true, activeColor, isSettingGridInventory: true });
};
